/**
 * Order-level charges & costs.
 *
 *   • Charges = the sales (revenue) side billed to the customer.
 *   • Costs   = the purchase side paid to suppliers/subcontractors.
 *
 * Default charge lines are seeded server-side at order creation; the panel
 * reads everything from the DB. At this level we only track the NET value per
 * line — qty/rate/VAT come later with invoicing. Persistence is an atomic
 * replace of the whole set per order.
 */
import {
  type ChargeCostSideValue,
  type ChargeCostTypeValue,
  type OrderChargeCost,
  type OrderChargeCostLineInput,
} from '@my/tms-api';

/** A single editable charge/cost line on an order (NET only for now). */
export interface ChargeCostLine {
  /** Server row id for a persisted line; a local `ccl-*` id for a fresh one. */
  id: string;
  /** Catalogue definition this line came from; null for an ad-hoc line. */
  definitionId: string | null;
  /** Display label — locked for catalogue lines, editable for ad-hoc ones. */
  name: string;
  /** Classification — snapshot for catalogue lines, `Custom` for ad-hoc. */
  type: ChargeCostTypeValue;
  /** NET (ex-VAT) amount. */
  net: number;
  /** Mirrors the definition flag — protected lines can't be removed. */
  isProtected: boolean;
  /**
   * Percentage rate for a derived line (fuel surcharge, or a %-of-base
   * additional), e.g. 10 = 10%. Null for plain money lines. Drives the
   * computed `net` on locked lines and the "+ X%" label suffix.
   */
  rate: number | null;
  /** True when `net` is computed (rate × base) and the operator can't edit it. */
  isLocked: boolean;
  /** True when this line feeds the fuel-surcharge base. */
  fsApplicable: boolean;
  /**
   * Charge side: who pays this one when not the order's payer. Set through its
   * own endpoint (not the footer Save, which leaves it alone); null = the order's.
   */
  billToPartyId?: string | null;
  /** On an invoice already — who pays it can no longer change. */
  invoiced?: boolean;
}

/** Build a fresh ad-hoc (Custom) charge/cost line with default rate flags. */
export function newCustomLine(): ChargeCostLine {
  return {
    id: newChargeCostLineId(),
    definitionId: null,
    name: '',
    type: 'Custom',
    net: 0,
    isProtected: false,
    rate: null,
    isLocked: false,
    fsApplicable: false,
  };
}

/** Sum the NET across a set of lines. */
export const sumNet = (lines: ChargeCostLine[]) => lines.reduce((s, l) => s + l.net, 0);

let _seq = 0;
/** Stable-enough client id for a new (unsaved) line. */
export const newChargeCostLineId = () => `ccl-${(_seq += 1)}`;

/** A line whose id was minted client-side hasn't been persisted yet. */
export const isServerId = (id: string) => !id.startsWith('ccl-');

/** Server DTO → editable line. */
export function lineFromDto(r: OrderChargeCost): ChargeCostLine {
  return {
    id: r.id,
    definitionId: r.chargeCostDefinitionId ?? null,
    name: r.description,
    type: r.type,
    net: r.net,
    isProtected: r.isProtected,
    rate: r.rate ?? null,
    isLocked: r.isLocked,
    fsApplicable: r.fsApplicable,
    billToPartyId: r.billToPartyId ?? null,
    invoiced: !!r.invoiceId,
  };
}

/** Editable line → replace-request input. Sequence is the row's display order. */
export function lineToInput(
  line: ChargeCostLine,
  side: ChargeCostSideValue,
  sequence: number,
): OrderChargeCostLineInput {
  return {
    id: isServerId(line.id) ? line.id : undefined,
    side,
    chargeCostDefinitionId: line.definitionId ?? undefined,
    description: line.name,
    type: line.type,
    net: line.net,
    sequence,
    rate: line.rate,
    isLocked: line.isLocked,
    fsApplicable: line.fsApplicable,
  };
}

/** Currency formatter with 2 decimals (invoicing precision). */
export function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}
